import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { SubscriptionGuide, SubscriptionModel } from '../interfaces';
import { SubscriptionContentKind, SubscriptionProduct, SubscriptionSeries, subscriptionProductNames } from '../interfaces.enums';
import { numberFormatted } from '../Formatters';
import { CHANGE_SUBSCRIPTION_CONTENT } from './subscriptions.graphql';
import { SubscriptionUsage } from './SubscriptionDialog';
import { allSeries, describeGiven, guideLabel, useSubscriptionGuides } from './content';
import { ChipPicker } from './ChipPicker';

const seriesOrder = (series?: SubscriptionSeries | null) => (series ? allSeries.indexOf(series) : allSeries.length);

interface Props {
  subscription: SubscriptionModel & { product: SubscriptionProduct };
  usage?: SubscriptionUsage;
  onClose(): void;
}

export function SubscriptionContentDialog({ subscription, usage, onClose }: Props) {
  const product = subscription.product;
  const current = subscription.content;
  const [kind, setKind] = useState<SubscriptionContentKind>(current?.kind ?? SubscriptionContentKind.Product);
  const [series, setSeries] = useState<SubscriptionSeries[]>(current?.series ?? []);
  const [docIds, setDocIds] = useState<number[]>(current?.docIds ?? []);
  const [includesOldVersions, setIncludesOldVersions] = useState(current?.includesOldVersions ?? true);
  const [error, setError] = useState<string | null>(null);

  const { guides, loading: loadingGuides, error: guidesError } = useSubscriptionGuides();
  const [change, { loading: saving }] = useMutation(CHANGE_SUBSCRIPTION_CONTENT, { refetchQueries: ['GetProductChanges'] });

  const inProduct = useMemo(() => (guides ?? []).filter((g) => g.product === product), [guides, product]);
  const byId = useMemo(() => new Map(inProduct.map((g) => [g.docId, g])), [inProduct]);
  // Only current guides can be added; the API refuses others too, unless the subscription already gives them.
  // Grouped by series in the suggestions, in the series' order, so the options must come in that order too.
  const choosable = useMemo(
    () =>
      inProduct
        .filter((g) => g.isCurrent)
        .sort((a, b) => seriesOrder(a.series) - seriesOrder(b.series) || a.number.localeCompare(b.number, 'nb', { numeric: true })),
    [inProduct],
  );
  // A chosen guide Fag no longer lists is kept, under its id, so saving does not drop it unnoticed.
  const chosen = docIds.map((id) => byId.get(id) ?? (guides ? missingGuide(id) : unknownGuide(id, loadingGuides)));
  const given = guides ? describeGiven({ kind, series, docIds, includesOldVersions }, product, guides) : undefined;
  const inUse = !!usage && (usage.licenses > 0 || usage.users > 0);

  const valid = (kind !== SubscriptionContentKind.Series || series.length > 0) && (kind !== SubscriptionContentKind.Guides || docIds.length > 0);

  const save = async () => {
    setError(null);
    try {
      await change({
        variables: {
          input: {
            subscriptionId: subscription.bksId,
            kind,
            series: kind === SubscriptionContentKind.Series ? series : [],
            docIds: kind === SubscriptionContentKind.Guides ? docIds : [],
            includesOldVersions,
          },
        },
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Kunne ikke lagre innholdet.');
    }
  };

  const seriesCount = (s: SubscriptionSeries) => inProduct.filter((g) => g.isCurrent && g.series === s).length;

  return (
    // Held at the top, so the dialog grows downwards rather than jumping when its height changes.
    <Dialog open fullWidth maxWidth="sm" onClose={onClose} sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}>
      <DialogTitle>Innhold i {subscription.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <FormControl>
            <FormLabel id="content-kind">Gir</FormLabel>
            <RadioGroup aria-labelledby="content-kind" value={kind} onChange={(e) => setKind(e.target.value as SubscriptionContentKind)}>
              <FormControlLabel value={SubscriptionContentKind.Product} control={<Radio />} label={`Hele ${subscriptionProductNames[product]}`} />
              {product === SubscriptionProduct.BFS && (
                <FormControlLabel value={SubscriptionContentKind.Series} control={<Radio />} label="Utvalgte serier" />
              )}
              <FormControlLabel value={SubscriptionContentKind.Guides} control={<Radio />} label="Utvalgte anvisninger" />
            </RadioGroup>
          </FormControl>

          {/* As tall as the series, so switching choices does not move what comes below. */}
          <Box sx={{ minHeight: 172 }}>
            {kind === SubscriptionContentKind.Product && (
              <Typography variant="body2" color="text.secondary">
                Alle anvisninger i {subscriptionProductNames[product]}, også nye når de publiseres.
              </Typography>
            )}

            {kind === SubscriptionContentKind.Series && (
              <FormControl>
                <FormLabel>Serier</FormLabel>
                <FormGroup>
                  {allSeries.map((s) => (
                    <FormControlLabel
                      key={s}
                      control={
                        <Checkbox
                          checked={series.includes(s)}
                          onChange={(e) => setSeries(e.target.checked ? [...series, s] : series.filter((x) => x !== s))}
                        />
                      }
                      label={guides ? `${s} (${numberFormatted(seriesCount(s)) ?? 0})` : s}
                    />
                  ))}
                </FormGroup>
                {series.length === 0 && <FormHelperText>Velg minst én serie.</FormHelperText>}
              </FormControl>
            )}

            {kind === SubscriptionContentKind.Guides && (
              <ChipPicker
                label="Anvisninger"
                placeholder="Søk på nummer eller tittel"
                options={choosable}
                value={chosen}
                onChange={(picked) => setDocIds(picked.map((g) => g.docId))}
                getKey={(g) => g.docId}
                getLabel={guideLabel}
                groupBy={product === SubscriptionProduct.BFS ? (g) => g.series ?? 'Uten serie' : undefined}
                loading={loadingGuides}
                error={!!guidesError}
                helperText={guidesError ? 'Fikk ikke hentet anvisningene fra Fag.' : docIds.length === 0 ? 'Velg minst én anvisning.' : undefined}
              />
            )}
          </Box>

          <FormControlLabel
            control={<Switch checked={includesOldVersions} onChange={(e) => setIncludesOldVersions(e.target.checked)} />}
            label="Historiske anvisninger"
          />

          <Typography variant="body2" color="text.secondary">
            {given ? given : loadingGuides ? 'Teller anvisningene …' : 'Fikk ikke hentet anvisningene fra Fag, så de er ikke telt.'}
          </Typography>

          {inUse && (
            <Alert severity="warning">
              Endringen gjelder med en gang for {numberFormatted(usage.licenses) ?? 0} lisenser og {numberFormatted(usage.users) ?? 0} brukere.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose}>
          Avbryt
        </Button>
        <Button variant="contained" disabled={!valid} loading={saving} onClick={save}>
          Lagre
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function missingGuide(docId: number): SubscriptionGuide {
  return { docId, number: `Id ${docId}`, title: 'finnes ikke i Fag', isCurrent: false, isWithdrawn: false };
}

function unknownGuide(docId: number, loading: boolean): SubscriptionGuide {
  return { docId, number: `Id ${docId}`, title: loading ? 'henter …' : 'ikke hentet', isCurrent: false, isWithdrawn: false };
}
